import { useEffect, useState, useCallback } from "react";
import { FaSave, FaSpinner, FaPaperPlane, FaBell } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./ManageAdmins.css";

const API_URL = process.env.REACT_APP_API_URL;

const emptyForm = {
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    from_email: "",
    from_name: "CIAL Airport Robot Portal",
    use_tls: true
};

const emptyAlertForm = {
    alert_emails: "",
    alert_on_emergency: true,
    alert_on_low_battery: true,
    alert_on_charging_started: true,
    alert_on_charging_completed: true,
    alert_on_charging_interrupted: false,
    alert_on_robot_offline: true,
    alert_on_robot_online: true
};

function EmailConfiguration() {

    const [form, setForm] = useState(emptyForm);

    const [isConfigured, setIsConfigured] = useState(false);

    const [passwordIsSet, setPasswordIsSet] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const [isSaving, setIsSaving] = useState(false);

    const [testEmail, setTestEmail] = useState("");

    const [isSendingTest, setIsSendingTest] = useState(false);

    const [alertForm, setAlertForm] = useState(emptyAlertForm);

    const [alertIsSaved, setAlertIsSaved] = useState(false);

    const [isAlertLoading, setIsAlertLoading] = useState(true);

    const [isAlertSaving, setIsAlertSaving] = useState(false);

    const loadAlertSettings = useCallback(async () => {

        setIsAlertLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/robot-alert-settings`);

            const data = await res.json();

            setAlertForm({
                alert_emails: data.alert_emails || "",
                alert_on_emergency: data.alert_on_emergency !== false,
                alert_on_low_battery: data.alert_on_low_battery !== false,
                alert_on_charging_started: data.alert_on_charging_started !== false,
                alert_on_charging_completed: data.alert_on_charging_completed !== false,
                alert_on_charging_interrupted: !!data.alert_on_charging_interrupted,
                alert_on_robot_offline: data.alert_on_robot_offline !== false,
                alert_on_robot_online: data.alert_on_robot_online !== false
            });

            setAlertIsSaved(!!data.is_saved);

        } catch (err) {

            console.error("Failed to load robot alert settings:", err);

            toast.error("Unable to load robot alert settings.");

        } finally {

            setIsAlertLoading(false);

        }

    }, []);

    const handleAlertChange = (e) => {

        const { name, value, type, checked } = e.target;

        setAlertForm({
            ...alertForm,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const handleAlertSave = async () => {

        setIsAlertSaving(true);

        try {

            const res = await fetch(`${API_URL}/portal/robot-alert-settings`, {

                method: "PUT",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify(alertForm)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

                loadAlertSettings();

            } else {

                toast.error(data.message || data.detail || "Failed to save robot alert settings.");

            }

        } catch (err) {

            console.error("Failed to save robot alert settings:", err);

            toast.error("Something went wrong while saving.");

        } finally {

            setIsAlertSaving(false);

        }

    };

    const load = useCallback(async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/email-settings`);

            const data = await res.json();

            setForm({
                smtp_host: data.smtp_host || "",
                smtp_port: data.smtp_port || 587,
                smtp_username: data.smtp_username || "",
                smtp_password: "",
                from_email: data.from_email || "",
                from_name: data.from_name || "CIAL Airport Robot Portal",
                use_tls: data.use_tls !== false
            });

            setIsConfigured(!!data.is_configured);

            setPasswordIsSet(!!data.smtp_password_is_set);

        } catch (err) {

            console.error("Failed to load email settings:", err);

            toast.error("Unable to load email settings.");

        } finally {

            setIsLoading(false);

        }

    }, []);

    useEffect(() => {

        load();
        loadAlertSettings();

    }, [load, loadAlertSettings]);

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const handleSave = async () => {

        if (!form.smtp_host || !form.from_email) {

            toast.warn("SMTP host and From email are required.");
            return;

        }

        setIsSaving(true);

        try {

            const res = await fetch(`${API_URL}/portal/email-settings`, {

                method: "PUT",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({
                    ...form,
                    smtp_port: Number(form.smtp_port) || 587
                })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

                load();

            } else {

                toast.error(data.message || data.detail || "Failed to save email settings.");

            }

        } catch (err) {

            console.error("Failed to save email settings:", err);

            toast.error("Something went wrong while saving.");

        } finally {

            setIsSaving(false);

        }

    };

    const handleSendTest = async () => {

        if (!testEmail.trim()) {

            toast.warn("Enter a recipient email for the test.");
            return;

        }

        setIsSendingTest(true);

        try {

            const res = await fetch(`${API_URL}/portal/email-settings/test`, {

                method: "POST",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({ to_email: testEmail.trim() })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

            } else {

                toast.error(data.message || data.detail || "Failed to send test email.");

            }

        } catch (err) {

            console.error("Failed to send test email:", err);

            toast.error("Something went wrong while sending the test email.");

        } finally {

            setIsSendingTest(false);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Email Configuration</h2>
                    <p className="page-subtitle">
                        SuperAdmin only. These SMTP settings power password-reset emails and the
                        "Send Email" feature. {isConfigured
                            ? "Currently configured and ready to send."
                            : "Not configured yet - emails won't send until this is filled in."}
                    </p>
                </div>

            </div>

            {

                isLoading ? (

                    <p className="empty-note"><FaSpinner className="spin" /> Loading...</p>

                ) : (

                    <div className="inline-form-container">

                        <div className="inline-form-grid">

                            <input
                                name="smtp_host"
                                placeholder="SMTP Host (e.g. smtp.gmail.com)"
                                value={form.smtp_host}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="smtp_port"
                                type="number"
                                placeholder="SMTP Port (e.g. 587)"
                                value={form.smtp_port}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="smtp_username"
                                placeholder="SMTP Username"
                                value={form.smtp_username}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="smtp_password"
                                type="password"
                                placeholder={passwordIsSet ? "•••••• (saved - leave blank to keep)" : "SMTP Password"}
                                value={form.smtp_password}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="from_email"
                                placeholder="From Email (e.g. noreply@yourdomain.com)"
                                value={form.from_email}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="from_name"
                                placeholder="From Name"
                                value={form.from_name}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <label className="checkbox-field">
                                <input
                                    type="checkbox"
                                    name="use_tls"
                                    checked={!!form.use_tls}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />
                                Use TLS (recommended - most providers require this on port 587)
                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="save-btn"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? <><FaSpinner className="spin" /> Saving...</> : <><FaSave /> Save Settings</>}
                            </button>

                        </div>

                        <div className="drawer-section" style={{ marginTop: "24px" }}>

                            <h4>Send a test email</h4>

                            <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "10px" }}>
                                Save your settings first, then confirm they actually work by sending
                                yourself a test email.
                            </p>

                            <div style={{ display: "flex", gap: "10px" }}>

                                <input
                                    placeholder="you@example.com"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    disabled={isSendingTest}
                                    style={{ flex: 1, padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px" }}
                                />

                                <button
                                    type="button"
                                    className="add-btn"
                                    onClick={handleSendTest}
                                    disabled={isSendingTest}
                                >
                                    {isSendingTest ? <><FaSpinner className="spin" /> Sending...</> : <><FaPaperPlane /> Send Test</>}
                                </button>

                            </div>

                        </div>

                        <div className="drawer-section" style={{ marginTop: "24px" }}>

                            <h4><FaBell /> Robot Fleet Alert Notifications</h4>

                            <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "10px" }}>
                                Automatically emails the addresses below when a robot reports an
                                Emergency Stop, Low Battery, or a charging state change. Uses the
                                SMTP settings above - save those first if you haven't already.
                                {alertIsSaved
                                    ? " Currently using the settings saved below."
                                    : " Not saved yet - currently using the .env defaults."}
                            </p>

                            {

                                isAlertLoading ? (

                                    <p className="empty-note"><FaSpinner className="spin" /> Loading...</p>

                                ) : (

                                    <>

                                        <div className="inline-form-grid">

                                            <input
                                                name="alert_emails"
                                                placeholder="Recipient emails, comma separated (e.g. ops@cial.com, security@cial.com)"
                                                value={alertForm.alert_emails}
                                                onChange={handleAlertChange}
                                                disabled={isAlertSaving}
                                                style={{ gridColumn: "1 / -1" }}
                                            />

                                        </div>

                                        <div className="inline-form-grid" style={{ marginTop: "10px" }}>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_emergency"
                                                    checked={!!alertForm.alert_on_emergency}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                🚨 Emergency button press
                                            </label>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_low_battery"
                                                    checked={!!alertForm.alert_on_low_battery}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                🔋 Battery low
                                            </label>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_charging_started"
                                                    checked={!!alertForm.alert_on_charging_started}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                🔌 Charging started
                                            </label>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_charging_completed"
                                                    checked={!!alertForm.alert_on_charging_completed}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                ✅ Charging completed
                                            </label>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_charging_interrupted"
                                                    checked={!!alertForm.alert_on_charging_interrupted}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                ⚠️ Charging interrupted early (optional, off by default)
                                            </label>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_robot_offline"
                                                    checked={!!alertForm.alert_on_robot_offline}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                🔴 Robot offline / power-off
                                            </label>

                                            <label className="checkbox-field">
                                                <input
                                                    type="checkbox"
                                                    name="alert_on_robot_online"
                                                    checked={!!alertForm.alert_on_robot_online}
                                                    onChange={handleAlertChange}
                                                    disabled={isAlertSaving}
                                                />
                                                🟢 Robot back online
                                            </label>

                                        </div>

                                        <div className="inline-form-actions" style={{ marginTop: "14px" }}>

                                            <button
                                                type="button"
                                                className="save-btn"
                                                onClick={handleAlertSave}
                                                disabled={isAlertSaving}
                                            >
                                                {isAlertSaving ? <><FaSpinner className="spin" /> Saving...</> : <><FaSave /> Save Alert Settings</>}
                                            </button>

                                        </div>

                                    </>

                                )

                            }

                        </div>

                    </div>

                )

            }

        </div>

    );

}

export default EmailConfiguration;
